import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, throwError as observableThrowError } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { VehicleTitleHistoryResultsModel } from '../components/inquiries/vehicle-title-history/vehicle-title-history-results.model';
import { OneTimeMailingModel } from '../components/shared/one-time-mailing/one-time-mailing.model';
import { OmegaRegistrationRenewalItemQueue } from '../models/omega-registration-renewal-queue-item.model';
import { OwnershipCompositeServiceEnum } from '../models/enums/owership-composite.enum';
import { RegistrationHistoryModel } from '../models/registration-history.model';
import { RegistrationModel } from '../models/registration.model';
import { BatchRegistrationCreditModel } from '../models/batch-registration-credit.model';

@Injectable()
export class TitleRegistrationService extends BaseApiService {

    /**
     * Gets all documents for the exemption on a given business transaction type.
     * @param exemptionTypeId: Exemption type associated to documents.
     * @param businessTransactionTypeId: Type of business transaction.
     * @return: List of document types associated to the exemption and business transaction type.
     */
    getExemptionDocuments = (exemptionTypeId: number, businessTransactionTypeId: number): Observable<any> => this.get('Foundation/Exemption/ExemptionType/' + exemptionTypeId + '/BusinessTransactionType/' + businessTransactionTypeId);

    /**
     * Gets all active exemptions for a customer.
     * @param customerNumberId: Customer to get exemptions for.
     * @return: List of active customer exemptions.
     */
    getActiveCustomerExemptions = (customerNumberId: number): Observable<any> => this.get('Foundation/Exemption/Customer/' + customerNumberId + '/Active');

    /**
     * Gets the customer characteristic associated the exemption type.
     * @param exemptionTypeId: Exemption type.
     * @return: Customer characteristic type id associated the exemption type.
     */
    getCustomerCharacteristicTypeIdByExemptionTypeId = (exemptionTypeId: number): Observable<any> => this.get('Foundation/Exemption/ExemptionType/' + exemptionTypeId + '/CustomerCharacteristic');

    /**
     * Gets the current ownership for the vehicle
     * @param vehicleId: Vehicle to get the current ownership for.
     */
    getActiveTitleByVehicle = (vehicleId: number) => this.get('TitleRegistration/Title/Vehicle/' + vehicleId + '/Active');

    /**
     * Gets all active registrations associated to the customer.
     * @param customerId: Customer to get the registrations for.
     */
    getRegistrationsByCustomer = (customerId: number): Observable<RegistrationModel[]> => this.get('TitleRegistration/Registration/Customer/' + customerId + '/Active');

    /**
     * Gets registration by Registration number.
     * @param registrationNumber: Registration number to get the registration for.
     */
    getRegistrationByRegistrationNumber = (registrationNumber: number): Observable<RegistrationModel> => this.get('TitleRegistration/Registration/RegistrationNumber/' + registrationNumber);

    /**
     * Gets all valid & active registrations associated with the customer.
     * @param customerId: Customer to get the registrations for.
     */
    getValidRegistrationsByCustomer = (customerId: number): Observable<RegistrationModel[]> => this.get('TitleRegistration/Registration/Customer/' + customerId + '/ValidRegistrations');

    /**
     * Gets all valid & active registrations associated with the customer.
     * @param customerId: Customer to get the registrations for.
     */
    getValidSpecialtyPlateRegistrationsByCustomer = (customerId: number): Observable<RegistrationModel[]> => this.get('TitleRegistration/Registration/Customer/' + customerId + '/ValidSpecialtyPlateRegistrations');

    // GetValidSpecialtyPlateRegistrationsByCustomer
    getCreditRegistrationsByCustomer = (customerId: number): Observable<RegistrationModel[]> => this.get('TitleRegistration/Registration/Customer/' + customerId + '/Credit');

    /**
     * Gets all registrations, past and present, associated with a given vehicleOwnershipId.
     * @param vehicleOwnershipId: The vehicleOwnershipId mentioned above.
     */
    getRegistrationHistoryByVehicleOwnershipId = (vehicleOwnershipId: number): Observable<RegistrationHistoryModel[]> => this.get('TitleRegistration/Registration/History/VehicleOwnership/' + vehicleOwnershipId);

    pushToAdjudicationQueue = (primarycustomerId: number, businessTransactionNumberId: number) => this.post('TitleRegistration/Registration/AdjudicationQueue/Customer/' + primarycustomerId + '/BusinessTransactionNumber/' + businessTransactionNumberId, null);

    setApportionedFlag = (vehicleId: number, isApportioned: boolean) => this.post('TitleRegistration/TitleRegistration/Vehicle/' + vehicleId + '/Apportioned/' + isApportioned, null);

    /**
     * Gets all active registrations associated to the vehicle.
     * @param vehicleId: Vehicle to get the registrations for.
     */
    getActiveRegistrationsByVehicle = (vehicleId: number) => this.get('TitleRegistration/Registration/Vehicle/' + vehicleId + '/Active');

    /**
     * Gets all non-expired registrations associated to the vehicle.
     * @param vehicleId: Vehicle to get the registrations for.
     */
    getRegistrationsByVehicle = (vehicleId: number) => this.get('TitleRegistration/Registration/Vehicle/' + vehicleId);

    getActiveVehicleOwnership = (vehicleId: number, ...ownershipCompositeServiceEnums: OwnershipCompositeServiceEnum[]) => this.post('TitleRegistration/TitleRegistration/VehicleOwnership/Vehicle/' + vehicleId + '/Active', ownershipCompositeServiceEnums);

    getVehicleOwnership = (vehicleOwnershipId: number, ...ownershipCompositeServiceEnums: OwnershipCompositeServiceEnum[]) => this.post('TitleRegistration/TitleRegistration/VehicleOwnership/' + vehicleOwnershipId, ownershipCompositeServiceEnums);

    getVehicleProhibitions = (vehicleId: number) => this.get('TitleRegistration/TitleRegistration/VehicleProhibitions/' + vehicleId);
    getVehicleExemptions = (vehicleOwnershipId: number) => this.get('TitleRegistration/Exemption/VehicleOwnership/' + vehicleOwnershipId);
    /**
     * Gets the printDate by BusinessTransactionNumber.
     * @param businessTransactionNumberId: transaction to get the registration for.
     */
    getPrintDataByBusinessTransactionNumber = (businessTransactionNumberId: number) => this.get('TitleRegistration/BusinessTransaction/' + businessTransactionNumberId + '/PrintReceipt');

    /**
     * Gets the printedPDF by BusinessTransactionNumber.
     * @param businessTransactionNumberId: transaction to get the registration for.
     */
    getPrintPDFByBusinessTransactionNumber = (businessTransactionNumberId: number, credentialTypeId: number) => this.get('TitleRegistration/BusinessTransaction/' + businessTransactionNumberId + '/Type/' + credentialTypeId + '/PrintPDF');

    /* public storePdf(model: any) {

        //var controller: string = this.getControllerName(model);
        return this.post('TitleRegistration/BusinessTransaction/GetDocumentDownloadId', model, this.replacer, 'text/plain')
            .catch(this.handleError);
    }*/
    private replacer(key, value) {
        if (value === '') {
            return undefined;
        }

        return value;
    }

    /**
     * Gets the oneTimeMailing address associated to a transaction.
     * @param id: titleId, registrationId. placardId etc.
     * @param businessTransactionTypeId: transaction type.
     */
    getOneTimeMailingBy(entityId: number, businessTransactionTypeId): Observable<OneTimeMailingModel[]> {
        return this.get('TitleRegistration/BusinessTransaction/' + entityId + '/OneTimeMailing/Type/' + businessTransactionTypeId);
    }

    /**
     * Verifies that a cash drawer is open for the current user.
     */
    verifyOpenCashDrawerCurrentUser = () => this.get('TitleRegistration/BusinessTransaction/VerifyOpenCashDrawer');

    /**
     * Gets the active registration with the plate number
     * @param plateNumber: Plate number on the registration.
     */
    getActiveRegistrationByPlateNumber = (plateNumber: string) => this.get('TitleRegistration/Registration/' + plateNumber + '/Active');

    /**
     * Gets a list of vehicles that the customer owns.
     * @param customerNumberId: Customer to get the vehicles for.
     */
    getCustomerVehicles(customerNumberId: number) {
        return this.get('titleregistration/titleregistration/CustomerVehicle/CustomerNumber/' + customerNumberId + '/Active');
    }

    getCustomerVehiclesByPaging(customerNumberId: number, pageNumber: number) {
        return this.get('titleregistration/titleregistration/CustomerVehicle/CustomerNumber/' + customerNumberId + '/Page/' + pageNumber);
    }

    /**
     * Gets a list of vehicles that the customer has active registration.
     * @param customerNumberId: Customer to get the vehicles for.
     */
    getCustomerVehiclesWithActiveRegistration(customerNumberId: number) {
        return this.get('titleregistration/titleregistration/CustomerVehicle/CustomerNumber/' + customerNumberId + '/ActiveRegistration')
            .pipe(
                map(r => {
                    const validationModels: any[] = r.result;
                    return validationModels.map((value, index, array) => value.result);
                })
            );
    }

    /**
     * Gets the title full view data.
     * @param vehicleId: the id of the vehicle.
     * @param customerNumber: the id of a customer that owns the vehicle.
     */
    getVehicleTitleFull(vehicleId: number, customerNumber: number) {
        return this.get(`TitleRegistration/Title/View/Vehicle/${vehicleId}/Customer/${customerNumber}`);
    }

    /**
     * Gets the Titles that are associated to a vehicle.
     * @param vehicleId: Vehicle to filter by.
     */
    getTitlesByVehicleId = (vehicleId: number) => this.get('TitleRegistration/Title/All/Vehicle/' + vehicleId);

    getTitleHistoryByVehicleId = (vehicleId: number) => this.get('TitleRegistration/Title/History/Vehicle/' + vehicleId);

    getVehicleHistoryDetailByTitle(titleNumber: string): Observable<VehicleTitleHistoryResultsModel> {
        return this.get('TitleRegistration/Title/Vehicle/History/Title/' + titleNumber);
    }

    getVehicleHistoryDetailByVin(vin: string): Observable<VehicleTitleHistoryResultsModel> {
        return this.get('TitleRegistration/Title/Vehicle/History/Vin/' + vin);
    }

    getVehicleHistoryDetailByVehicleId(vehicleId: number) {
        return this.get('TitleRegistration/Title/Vehicle/History/VehicleId/' + vehicleId);
    }

    getEstimatedRegistrationFee = (estimatedRegistration: any) => this.post('TitleRegistration/Registration/EstimateRegistrationFee', estimatedRegistration);

    getEstimatedRegistrationCredit = (estimatedRegistrationCredit: any) => this.post('TitleRegistration/Registration/EstimateRegistrationCredit', estimatedRegistrationCredit);

    searchCreditRegistrations(model: any, pageNumber: number, pageSize: number) {
        return this.post('TitleRegistration/Registration/SearchCreditRegistrations/PageSize/' + pageSize + '/Page/' + pageNumber, model);
    }

    updateCreditRegistration(registrationId: number, registrationStatusTypeId: number, creditEligibilityStatusTypeId: number) {
        return this.put('TitleRegistration/Registration/UpdateCreditRegistration/' + registrationId + '/RegistrationStatusTypeId/' + registrationStatusTypeId + '/creditEligibilityStatusTypeId/' + creditEligibilityStatusTypeId, null);
    }

    private handleError(error: HttpErrorResponse) {
        return observableThrowError(error.error || 'Server error');
    }

    getRegistrationFeeSummary(vehicleRegistration: any) {
        return this.post('TitleRegistration/TitleRegistration/Vehicle/FeeSummary', vehicleRegistration);
    }

    getMCORegistrationStartDate = (vehicleOwnershipDate: any) => this.post('TitleRegistration/Registration/MCO/StartDate', vehicleOwnershipDate);

    queueOmegaRegistrationRenewal = (omegaRegistrationRenewalItemQueue: OmegaRegistrationRenewalItemQueue) => this.post('TitleRegistration/Registration/Omega/Renewal/Queue', omegaRegistrationRenewalItemQueue);

    async queueRegistrationCredit(batchRegistrationCredit: BatchRegistrationCreditModel): Promise<void> {
        const obs$ = this.post('TitleRegistration/Registration/RegistrationCredit/Queue', batchRegistrationCredit);
        return await lastValueFrom(obs$);
    }
}
